import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay, subDays, format } from "date-fns";
import { es } from "date-fns/locale";

export interface DailyData {
  date: Date;
  dayName: string;
  dayNumber: number;
  count: number;
}

export interface WeeklyStats {
  days: DailyData[];
  totalWeek: number;
  averagePerDay: number;
}

interface UseWeeklyAttendanceStatsOptions {
  gymId: string;
  enabled?: boolean;
}

export const useWeeklyAttendanceStats = ({
  gymId,
  enabled = true,
}: UseWeeklyAttendanceStatsOptions) => {
  const today = new Date();
  const weekStart = startOfDay(subDays(today, 6));
  const weekEnd = endOfDay(today);

  return useQuery({
    queryKey: ["weekly-attendance-stats", gymId, weekStart.toISOString()],
    queryFn: async (): Promise<WeeklyStats> => {
      const { data, error } = await supabase
        .from("gym_attendance_logs")
        .select("check_in_at")
        .eq("gym_id", gymId)
        .gte("check_in_at", weekStart.toISOString())
        .lte("check_in_at", weekEnd.toISOString());

      if (error) throw error;

      // Initialize last 7 days
      const daysMap = new Map<string, number>();
      const daysData: DailyData[] = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = subDays(today, i);
        const dateKey = format(date, "yyyy-MM-dd");
        daysMap.set(dateKey, 0);
        daysData.push({
          date,
          dayName: format(date, "EEE", { locale: es }).charAt(0).toUpperCase() + 
                   format(date, "EEE", { locale: es }).slice(1, 3),
          dayNumber: date.getDate(),
          count: 0,
        });
      }

      // Count entries per day
      (data || []).forEach(record => {
        const dateKey = format(new Date(record.check_in_at), "yyyy-MM-dd");
        if (daysMap.has(dateKey)) {
          daysMap.set(dateKey, (daysMap.get(dateKey) || 0) + 1);
        }
      });

      // Update counts in daysData
      daysData.forEach((day, index) => {
        const dateKey = format(day.date, "yyyy-MM-dd");
        daysData[index].count = daysMap.get(dateKey) || 0;
      });

      const totalWeek = data?.length || 0;
      const averagePerDay = totalWeek / 7;

      return {
        days: daysData,
        totalWeek,
        averagePerDay: Math.round(averagePerDay * 10) / 10,
      };
    },
    enabled: enabled && !!gymId,
    refetchInterval: 30000,
  });
};
